import { ButtonERP } from "@finanedi.es/storybook"
import { CONCILIACION_CONCILIAR_MOVIMIENTOS_COMMAND_DTO } from "api/DTOs/ConciliacionConciliarMovimientosCommandDto"
import { EntidadConciliableReadingDtoProps } from "api/DTOs/EntidadConciliableReadingDto"
import { GetConciliacionBancariaByIdDtoProps } from "api/DTOs/GetConciliacionBancariaByIdDto"
import { ESTADO_CONCILIACION } from "api/Types/EstadoConciliacion"
import { TIPO_CONCILIACION } from "api/Types/TipoConciliacion"
import { TFunction } from "i18next"
import React, { useState } from "react"
import { frontendCurrency } from "utils/formatCurrency"
import ConciliacionManualForzadaTemplate from "../components/ForzadaTemplate"
import PanelConciliables from "../components/PanelConciliables"
import TablaConciliadas from "../components/TablaConciliadas"
import { TAB, TabItem, calculateImportePendiente } from "../constants/constants"
import {
  HandleConfirmManualConciliacionProps,
  TablaConciliarMovimientosDataProps,
} from "../hooks/useManualRowSelection"

export type SelectedDocumentsProps = EntidadConciliableReadingDtoProps & {
  tabKey: (typeof CONCILIACION_CONCILIAR_MOVIMIENTOS_COMMAND_DTO)[keyof typeof CONCILIACION_CONCILIAR_MOVIMIENTOS_COMMAND_DTO]
}

interface ColumnRightProps {
  conciliacionBancariaById: GetConciliacionBancariaByIdDtoProps | undefined
  isUpperSectionExpanded: boolean
  toggleUpperSection: () => void
  isUpperSectionExpandedSecond: boolean
  toggleUpperSectionSecond: () => void
  t: TFunction
  conciliarMovimientoData?: TablaConciliarMovimientosDataProps[] | null
  conciliarMovimientoStatus: boolean
  handleConfirmManualConciliacion: (
    props: HandleConfirmManualConciliacionProps
  ) => Promise<void | boolean>
  handleForzarConciliacion: () => void
  handleRestaurarConciliacion: () => void
  handleCrearAsiento: () => void
  handleDeleteConciliacion: () => void
  datePadre: [Date, Date | null]
  refrescar: number
  selectedNewRight: Array<SelectedDocumentsProps>
  setSelectedNewRight: React.Dispatch<
    React.SetStateAction<Array<SelectedDocumentsProps>>
  >
}

const ColumnRight: React.FC<ColumnRightProps> = ({
  t,
  conciliarMovimientoData,
  conciliarMovimientoStatus,
  handleConfirmManualConciliacion,
  handleCrearAsiento,
  handleForzarConciliacion,
  handleRestaurarConciliacion,
  isUpperSectionExpanded,
  isUpperSectionExpandedSecond,
  toggleUpperSection,
  toggleUpperSectionSecond,
  conciliacionBancariaById,
  handleDeleteConciliacion,
  datePadre,
  refrescar,
  selectedNewRight,
  setSelectedNewRight,
}) => {
  const [rightTabSelected, setRightTabSelected] = useState<TabItem>(
    TAB.PAGOS_COBROS
  )

  const [conciliationId, setConciliationId] = useState<string | null>()
  const [isLowerSectionExpanded, setIsLowerSectionExpanded] = useState(false)

  const toggleLowerSection = () => {
    setIsLowerSectionExpanded(!isLowerSectionExpanded)
  }

  const isForzada =
    conciliarMovimientoData?.[0]?.tipoConciliacion === TIPO_CONCILIACION.FORZADA
  const hasConciliatedDocuments = Boolean(
    conciliarMovimientoData?.[0]?.conciliacionBancariaId
  )
  const hasMovementsToConciliate = Boolean(conciliarMovimientoStatus)
  const hasMovementsContables = Boolean(selectedNewRight?.length)

  const isConciliated = !(
    hasMovementsToConciliate &&
    (hasConciliatedDocuments || isForzada)
  )
  const canConciliate =
    hasMovementsToConciliate &&
    conciliarMovimientoData?.[0]?.estadoConciliacion ===
      ESTADO_CONCILIACION.SIN_CONCILIAR

  const hasFlujosTesoreriaToConciliate =
    selectedNewRight
      .filter((row) => row?.tabKey === "flujoTesoreriaIds" && row?.id)
      .map((row) => String(row.id)).length > 0

  const hasOnlyFlujoTesoreriaIds =
    selectedNewRight.filter(
      (row) => row?.tabKey !== "flujoTesoreriaIds" && row?.id
    ).length === 0

  const canBeForced =
    hasMovementsToConciliate ||
    (hasFlujosTesoreriaToConciliate && hasOnlyFlujoTesoreriaIds)

  return (
    <div className='tw-flex tw-max-h-full tw-h-full tw-flex-col tw-justify-between tw-bg-white'>
      {conciliacionBancariaById ? (
        <TablaConciliadas
          isUpperSectionExpanded={isUpperSectionExpanded}
          toggleUpperSection={toggleUpperSection}
          t={t}
          conciliacionBancariaById={conciliacionBancariaById}
          isUpperSectionExpandedSecond={isUpperSectionExpandedSecond}
          toggleUpperSectionSecond={toggleUpperSectionSecond}
        />
      ) : isForzada ? (
        <div className='tw-w-full tw-h-full tw-flex tw-flex-row tw-items-center tw-justify-center'>
          <ConciliacionManualForzadaTemplate
            onConfirm={handleRestaurarConciliacion}
          />
        </div>
      ) : (
        <section className='tw-relative tw-flex tw-flex-1 tw-flex-col tw-overflow-hidden'>
          <div className='tw-flex-1 tw-overflow-auto tw-bg-gray-100 tw-transition-opacity tw-duration-100'>
            <div className='tw-h-full tw-flex-1 tw-items-center tw-overflow-hidden tw-transition-all tw-duration-100 tw-ease-in-out'>
              <PanelConciliables
                datePadre={datePadre}
                importePendiente={
                  conciliarMovimientoData?.[0]?.importePendiente
                }
                conciliationId={conciliationId}
                handleCrearAsiento={handleCrearAsiento}
                tabSelected={rightTabSelected}
                setTabSelected={setRightTabSelected}
                isLowerSectionExpanded={isLowerSectionExpanded}
                selectedNewRight={selectedNewRight}
                setSelectedNewRight={setSelectedNewRight}
                setIsLowerSectionExpanded={setIsLowerSectionExpanded}
                toggleLowerSection={toggleLowerSection}
                refrescar={refrescar}
              />
            </div>
          </div>
        </section>
      )}
      {(!isForzada || canConciliate) && (
        <div
          className='tw-flex tw-h-20 tw-items-center tw-justify-between tw-bg-white tw-p-1 data-[forzada=false]:tw-border-t'
          data-forzada={isForzada}
          data-has-sections={
            canConciliate ||
            (isConciliated && hasConciliatedDocuments) ||
            !!conciliacionBancariaById
          }
        >
          {!isForzada &&
            (!conciliacionBancariaById ? (
              <span className='tw-flex tw-gap-2 tw-pl-6 tw-font-medium'>
                {t("bancos.conciliacion.rightColumn.pendingBalance", {
                  balance: frontendCurrency(
                    calculateImportePendiente(
                      conciliarMovimientoData,
                      selectedNewRight
                    )
                  ),
                })}
              </span>
            ) : (
              <div className='tw-flex tw-flex-row tw-justify-end tw-items-center tw-w-full tw-px-4 tw-h-14'>
                <ButtonERP
                  customize={{
                    type: "secondary",
                    size: "md",
                  }}
                  onClick={() => {
                    handleDeleteConciliacion()
                  }}
                  label={`${t(
                    "bancos.conciliacion.rightColumn.labelUnreconciled"
                  )}`}
                />
              </div>
            ))}
          {(canConciliate || (hasMovementsContables && canBeForced)) && (
            <div className='tw-flex tw-cursor-pointer tw-gap-px tw-pr-4'>
              <button
                type='button'
                value={String(
                  t("bancos.conciliacion.manualTable.labeMarcarConciliado")
                )}
                className='hover:tw-bg-primary-dark !tw-bg-secondary tw-p-2 tw-px-4 tw-font-normal tw-text-white tw-transition-colors tw-duration-100 tw-ease-in-out !tw-rounded'
                onClick={async () => {
                  if (!hasMovementsContables && !isForzada) {
                    return handleForzarConciliacion()
                  }

                  if (hasMovementsContables && !canConciliate && !isForzada) {
                    return handleForzarConciliacion()
                  }

                  if (!isForzada) {
                    try {
                      await handleConfirmManualConciliacion({
                        selectedDocuments: selectedNewRight,
                        idExterno: conciliarMovimientoData?.[0],
                      }).then(() => {
                        setConciliationId(
                          conciliarMovimientoData?.[0]?.idExterno
                        )
                      })
                    } catch (error) {
                      // eslint-disable-next-line no-console
                      console.error(error)
                    }
                    return
                  }
                  return handleRestaurarConciliacion()
                }}
              >
                {isForzada
                  ? t(
                      "bancos.conciliacion.manualTable.labelRestartConciliacion"
                    )
                  : canConciliate && hasMovementsContables
                  ? t("bancos.conciliacion.modal.reconcileButton")
                  : t(
                      "bancos.conciliacion.manualTable.labelForzarConciliacion"
                    )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ColumnRight
